
import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { LogOut, LogIn } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.includes("admin");
  const [user, setUser] = useState<any>(null);
  const [isAdminUser, setIsAdminUser] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkAdminStatus(session.user.id);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkAdminStatus(session.user.id);
      } else {
        setIsAdminUser(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async (userId: string) => {
    const { data, error } = await supabase
      .from('admin_profiles')
      .select('id')
      .eq('id', userId)
      .single();
    
    if (error) {
      console.error('Error checking admin status:', error);
      setIsAdminUser(false);
      return;
    }
    
    setIsAdminUser(!!data);
  };

  const handleLogout = async () => {
    try {
      // First, attempt to get the current session
      const { data: { session } } = await supabase.auth.getSession();
      
      // If no session exists, just redirect to auth page
      if (!session) {
        setUser(null);
        navigate("/auth");
        return;
      }

      // Attempt to sign out
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        // If we get a user_not_found error, just clear the local state and redirect
        if (error.message.includes('user_not_found')) {
          setUser(null);
          navigate("/auth");
          return;
        }
        throw error;
      }

      toast.success("Successfully signed out!");
      navigate("/auth");
    } catch (error: any) {
      console.error('Logout error:', error);
      // In case of any error, clear local state and redirect to auth
      setUser(null);
      navigate("/auth");
    }
  };

  return (
    <nav className="w-full bg-white border-b border-gray-100 py-4 px-6 flex items-center justify-between">
      <Link to="/" className="flex items-center space-x-2">
        <span className="text-3xl font-bold text-primary">Volleyball</span>
        <span className="text-2xl">🏐</span>
      </Link>
      <div className="flex items-center space-x-6">
        {isAdminUser && (
          <div className="flex flex-col bg-gray-100 rounded-lg p-0.5 space-y-0.5">
            <button
              onClick={() => navigate("/")}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${
                !isAdmin
                  ? "bg-[#9b87f5] text-white"
                  : "text-[#8E9196] hover:text-gray-700"
              }`}
            >
              Player View
            </button>
            <button
              onClick={() => navigate("/admin")}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${
                isAdmin
                  ? "bg-[#9b87f5] text-white"
                  : "text-[#8E9196] hover:text-gray-700"
              }`}
            >
              Admin View
            </button>
          </div>
        )}
        {user ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-destructive hover:text-destructive/90 hover:bg-destructive/10"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/auth")}
            className="text-primary hover:text-primary/90 hover:bg-primary/10"
          >
            <LogIn className="w-4 h-4" />
          </Button>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
